import { useState } from 'react';
import { CheckIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The environment variable a field is saved as, on the field's own label row
 * (`GH_HOST`), so a developer sees the name their scripts will read where they
 * type its value. Click copies the name.
 *
 * Not part of the label: the label stays plain ("Host"), and the input is
 * associated to the variable through `aria-describedby`, so a screen reader hears
 * "saved as GH_HOST" after the field name instead of one run-on label.
 */
export function EnvTag({
  name,
  id,
  className,
}: {
  name: string;
  /** Lets the input point at this with `aria-describedby`. */
  id?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      id={id}
      onClick={() => {
        void navigator.clipboard
          .writeText(name)
          .then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          })
          .catch(() => {
            // Clipboard access can be refused (insecure context, permissions);
            // the name is on screen either way.
          });
      }}
      title="Copy variable name"
      className={cn(
        'text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-sm font-mono text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none',
        className,
      )}
    >
      <span className="sr-only">Saved as </span>
      {name}
      {copied ? <CheckIcon aria-label="Copied" className="size-3" /> : null}
    </button>
  );
}
